import { memo, useCallback, useEffect, useState, type ChangeEvent } from 'react'
import Switch from 'react-switch'
import { cn, Select } from '@quickmd-llc/design-system'
import { isActiveEllipsisCheck, visitLengthDropdownOption } from '../../../utils/common'
import type { ProductivityListItemProps, ProductivityNormalizedServiceType, RawProductivityServiceType } from './types'

export type {
  ProductivityForEditShape,
  ProductivityForEditUpdater,
  ProductivityNormalizedServiceType,
  ProductivityNormalizedVisitTypes,
  ProductivityVisitTypeItem,
  RawProductivityServiceType,
  ServiceTypeArrayUpdater,
  TooltipState,
} from './types'

// Duration options: 1-min steps 5–14, then 5-min steps 15–60 (matches visitLengthDropdownOption).
const DURATION_OPTIONS: number[] = visitLengthDropdownOption()

// Thin wrapper so call sites stay readable — setter and updater share the raw-array type.
function callWithRawUpdater(
  setter: (updater: (prev: RawProductivityServiceType[]) => RawProductivityServiceType[]) => void,
  updater: (prev: RawProductivityServiceType[]) => RawProductivityServiceType[],
) {
  setter(updater)
}

const ProductivityListItem = memo(
  ({
    visitType,
    normalizedVisitTypes,
    isViewMode,
    setSelectedServiceTypeArray,
    serviceTypeId,
    setExtraLongWordsToolTip,
    selectedServiceType,
    setProductivityForEdit,
  }: ProductivityListItemProps) => {
    const { Name, Duration, visitTypeInitialDuration, VisitTypeId } = visitType
    const initalVisitTypeValue = selectedServiceType?.VisitTypes?.entities?.[VisitTypeId ?? '']

    const normalizedVisitType = normalizedVisitTypes.find(
      ({ ServiceTypeId }: ProductivityNormalizedServiceType) => ServiceTypeId === serviceTypeId,
    )?.VisitTypes.entities[VisitTypeId ?? '']

    const [isEditDurationMode, setIsEditDurationMode] = useState(!!normalizedVisitType?.customDuration)
    const [isNameToolTipActive, setIsNameToolTipActive] = useState(false)
    const [durationDynamicValue, setDurationDynamicValue] = useState<number | undefined>(
      normalizedVisitType?.Duration ?? Duration,
    )

    const setDropdownDurationDynamicValue = useCallback(
      (newValue: number, isHasBeenDisabled = false) => {
        setDurationDynamicValue(newValue)
        if (setSelectedServiceTypeArray) {
          callWithRawUpdater(setSelectedServiceTypeArray, (prev) =>
            prev.map((serviceType) =>
              serviceType.ServiceTypeId === serviceTypeId ?
                {
                  ...serviceType,
                  VisitTypes: serviceType.VisitTypes.map((visit) =>
                    visit.VisitTypeId === VisitTypeId ?
                      { ...visit, customDuration: newValue, adjustToggleValue: isEditDurationMode }
                    : visit,
                  ),
                }
              : serviceType,
            ),
          )
        }
        setProductivityForEdit?.((prev) => ({
          ...prev,
          VisitTypes: prev.VisitTypes.map((visit) =>
            visit.VisitTypeId === VisitTypeId ?
              {
                ...visit,
                customDuration: newValue,
                isNewCustomDurationValueWasAdded: newValue !== visit.Duration,
                isCustomDurationWasDisabled: !!isHasBeenDisabled,
                isAdjusted: isHasBeenDisabled ? visit.isAdjusted : newValue !== visit.Duration,
              }
            : visit,
          ),
        }))
      },
      [VisitTypeId, serviceTypeId, setProductivityForEdit, setSelectedServiceTypeArray, isEditDurationMode],
    )

    const toggleIsEditDurationMode = useCallback(
      (newValue: boolean) => {
        setIsEditDurationMode(newValue)
        if (newValue) {
          if (setSelectedServiceTypeArray) {
            callWithRawUpdater(setSelectedServiceTypeArray, (prev) =>
              prev.map((serviceType) =>
                serviceType.ServiceTypeId === serviceTypeId ?
                  {
                    ...serviceType,
                    VisitTypes: serviceType.VisitTypes.map((visit) =>
                      visit.VisitTypeId === VisitTypeId ?
                        { ...visit, customDuration: Duration, adjustToggleValue: newValue }
                      : visit,
                    ),
                  }
                : serviceType,
              ),
            )
          }
          setProductivityForEdit?.((prev) => ({
            ...prev,
            VisitTypes: prev.VisitTypes.map((visit) =>
              visit.VisitTypeId === VisitTypeId ?
                {
                  ...visit,
                  customDuration: visit.visitTypeInitialDuration,
                  Duration: visit.visitTypeInitialDuration,
                  isCustomDurationWasDisabled: false,
                  isNewCustomDurationValueWasAdded: true,
                  isAdjusted: !visit.isWasInitiallyAdjusted,
                  isNeedToUpdateDuration: normalizedVisitType?.Duration !== visit.visitTypeInitialDuration,
                }
              : visit,
            ),
          }))
        } else {
          setDurationDynamicValue(Duration)
          if (setSelectedServiceTypeArray) {
            callWithRawUpdater(setSelectedServiceTypeArray, (prev) =>
              prev.map((serviceType) =>
                serviceType.ServiceTypeId === serviceTypeId ?
                  {
                    ...serviceType,
                    VisitTypes: serviceType.VisitTypes.map((visit) =>
                      visit.VisitTypeId === VisitTypeId ?
                        { ...visit, customDuration: undefined, adjustToggleValue: true }
                      : visit,
                    ),
                  }
                : serviceType,
              ),
            )
          }
          setProductivityForEdit?.((prev) => ({
            ...prev,
            VisitTypes: prev.VisitTypes.map((visit) => {
              if (visit.VisitTypeId === VisitTypeId) {
                setDropdownDurationDynamicValue(visit.visitTypeInitialDuration ?? Duration ?? 0, true)
                return {
                  ...visit,
                  customDuration: undefined,
                  Duration: visit.visitTypeInitialDuration,
                  isCustomDurationWasDisabled: !!visit.isWasInitiallyAdjusted,
                  isNewCustomDurationValueWasAdded: false,
                  isAdjusted: !!visit.isWasInitiallyAdjusted,
                  isNeedToUpdateDuration: normalizedVisitType?.Duration !== visit.visitTypeInitialDuration,
                }
              }
              return visit
            }),
          }))
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- normalizedVisitType is derived from props already in deps; adding normalizedVisitType.Duration would re-create the callback on every service-type load, diverging from original JSX behaviour
      [
        Duration,
        VisitTypeId,
        serviceTypeId,
        setDropdownDurationDynamicValue,
        setProductivityForEdit,
        setSelectedServiceTypeArray,
      ],
    )

    useEffect(() => {
      if (!isViewMode && setSelectedServiceTypeArray) {
        callWithRawUpdater(setSelectedServiceTypeArray, (prev) =>
          prev.map((serviceType) =>
            serviceType.ServiceTypeId === serviceTypeId ?
              {
                ...serviceType,
                VisitTypes: serviceType.VisitTypes.map((visit) =>
                  visit.VisitTypeId === VisitTypeId ?
                    {
                      ...visit,
                      customDuration: normalizedVisitType?.customDuration,
                      Duration: initalVisitTypeValue?.Duration,
                    }
                  : visit,
                ),
              }
            : serviceType,
          ),
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to seed the array-form entry; deps intentionally omitted to avoid re-seeding on every prop change
    }, [])

    useEffect(() => {
      isActiveEllipsisCheck(
        isViewMode ?
          `productivity-visit-type-item-name-view-mode${VisitTypeId}`
        : `productivity-visit-type-item-name-edit-mode${VisitTypeId}`,
        setIsNameToolTipActive,
      )
    }, [Name, VisitTypeId, isViewMode])

    const onMouseOverHandler = (e: React.MouseEvent) => {
      if (isNameToolTipActive) {
        setExtraLongWordsToolTip?.({
          top: e.clientY,
          left: e.clientX,
          title: Name ?? '',
        })
      }
    }
    const onFocusHandler = (e: React.FocusEvent<HTMLElement>) => {
      if (isNameToolTipActive) {
        const rect = e.currentTarget.getBoundingClientRect()
        setExtraLongWordsToolTip?.({
          top: rect.bottom,
          left: rect.left,
          title: Name ?? '',
        })
      }
    }
    const onMouseLeaveHandler = () => {
      if (isNameToolTipActive) {
        setExtraLongWordsToolTip?.(null)
      }
    }

    const handleDurationChange = useCallback(
      (e: ChangeEvent<HTMLSelectElement>) => {
        const parsed = Number(e.target.value)
        if (!Number.isNaN(parsed)) {
          setDropdownDurationDynamicValue(parsed)
        }
      },
      [setDropdownDurationDynamicValue],
    )

    const productivityListItemView =
      isViewMode ?
        <>
          <div
            onMouseOver={onMouseOverHandler}
            onFocus={onFocusHandler}
            onMouseLeave={onMouseLeaveHandler}
            onBlur={onMouseLeaveHandler}
            id={`productivity-visit-type-item-name-view-mode${VisitTypeId}`}
            className='drop-safari-tooltip max-w-[65vw] cursor-default overflow-hidden text-ellipsis whitespace-nowrap font-semibold'>
            {Name}
          </div>
          <div className='text-center'>: {Duration} minutes</div>
        </>
      : <>
          <div
            className='drop-safari-tooltip max-w-[145px] cursor-default overflow-hidden text-ellipsis whitespace-nowrap font-semibold'
            onMouseOver={onMouseOverHandler}
            onFocus={onFocusHandler}
            onMouseLeave={onMouseLeaveHandler}
            onBlur={onMouseLeaveHandler}
            id={`productivity-visit-type-item-name-edit-mode${VisitTypeId}`}>
            {Name}
          </div>
          <div className='text-center'>{visitTypeInitialDuration ?? Duration} minutes</div>
          <div className='pl-[18px] text-center'>
            <Switch
              onChange={toggleIsEditDurationMode}
              checked={isEditDurationMode}
              offColor='#B0BDCB'
              onColor='#00A2B2'
              height={30}
              width={66}
              uncheckedIcon={
                <div className='-mt-px ml-[11px] flex h-full items-center text-base font-normal tracking-[1.2px] text-white'>
                  no
                </div>
              }
              checkedIcon={
                <div className='ml-[7px] flex items-center pt-px text-base font-normal tracking-[1.2px] text-white'>
                  yes
                </div>
              }
              activeBoxShadow='#ffffff'
            />
          </div>
          <div>
            <Select
              disabled={!isEditDurationMode}
              value={String(durationDynamicValue ?? '')}
              onChange={handleDurationChange}
              id={`durationDynamicValue${VisitTypeId}`}>
              {DURATION_OPTIONS.map((mins) => (
                <option key={mins} value={String(mins)}>
                  {mins} Mins
                </option>
              ))}
            </Select>
          </div>
        </>

    return (
      <div
        className={cn(
          'mt-4 grid cursor-default grid-cols-[1.5fr_1.3fr_1fr_1.5fr] items-center text-lg font-normal leading-6 tracking-[0.05em] text-[#141414]',
          isViewMode && 'grid-cols-[fit-content(100%)_fit-content(100%)_1fr]',
        )}
        onScroll={onMouseLeaveHandler}>
        {productivityListItemView}
      </div>
    )
  },
)

ProductivityListItem.displayName = 'ProductivityListItem'

export { ProductivityListItem }
export default ProductivityListItem
